import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import lodash from 'lodash'
import React, { useState } from 'react'

import CourseModal from 'components/Editor/cource/components/CourseModal'
import { SHELL_SIZE_KEYS } from 'components/Editor/types/data.constants'
import Icon from 'components/uiKit/Icon'
import { preventDefault } from 'constants/commonConstans'
import { DeviceMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getIsInteractiveMode } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { useFileByUsage } from 'utils/files'
import { testProps } from 'utils/test/qaData'

import ResponsiveImage from '../../ResponsiveImage'
import { useElementRootStyles } from '../../hooks/useElementRootStyles'
import { getUrlMedia } from '../../utils/getUrlImage'
import * as s from './ImageElement.module.scss'
import Zoom from './Zoom'
import ImageFallback from './assets/imageDemo.svg'
import type { ImageElementType } from './imageTypes'
import ZoomButtonStyled from './styled/ZoomButtonStyled'

const ImageElView: ImageElementType = ({ element, styles, mode, block, isV2 }) => {
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.image.type,
      elementId: element.id,
    },
  )
  const [isFullSize, setIsFullSize] = useState(false)
  const isInteractive = useProjectContext(getIsInteractiveMode)
  const image = element.value.image
  const zoomable = element.value.zoomable

  // const file = useImageWithCrop(element.value?.image)
  const isEditor = mode.previewMode === PreviewMode.editor
  const file = useFileByUsage(element.value?.image, FileUsageTypeEnum.image)
  const { params } = element.value.image || {}
  const { round } = params || {}
  const desktopMode = mode.deviceMode === DeviceMode.desktop
  const zoomImage = zoomable && isInteractive
  const style = {
    maxWidth: '100%',
    maxHeight: '100%',
    ...lodash.pick(styles.shell, SHELL_SIZE_KEYS),
    ...(round && { borderRadius: '50%' }),
    ...(zoomImage && desktopMode && { cursor: 'zoom-in' }),
  }

  const rootStyles = useElementRootStyles(styles.root, styles.image, styles.effects, style)
  const imagePath = getUrlMedia(image, file?.path) || ImageFallback

  const onZoom = () => {
    if (zoomImage) {
      const data = entityAnalyticsConfig.zoomStarted({
        resultExtensions: { zoomIn: true },
        objectExtensions: baseObjectExtensions,
        elementId: element.id,
      })
      sendXapiData(data)
      setIsFullSize(true)
    }
  }

  const zoomStyle: React.CSSProperties = lodash.pick(rootStyles, ['borderRadius'])

  return (
    <>
      {isEditor && isV2 && !(image?.id || image?.url) && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <img alt='' className={s.fallback} onMouseDown={preventDefault} src={ImageFallback} />
      )}
      {zoomImage && (
        <CourseModal
          ariaLabel='Zoomed image'
          component={Zoom}
          mode={mode}
          onClose={() => {
            const data = entityAnalyticsConfig.zoomEnded({
              resultExtensions: { zoomOut: true },
              objectExtensions: baseObjectExtensions,
              elementId: element.id,
            })
            sendXapiData(data)
            setIsFullSize(false)
          }}
          props={{ imagePath, imageStyle: zoomStyle }}
          visible={isFullSize}
        />
      )}
      <ResponsiveImage
        onClick={onZoom}
        overlay={element.value.overlay}
        style={rootStyles}
        usage={image}
        {...testProps({ el: 'element-image', fileName: file?.name })}
      />
      {zoomImage && (
        <ZoomButtonStyled
          $isDesktop={desktopMode}
          onClick={onZoom}
          {...testProps({ el: 'button', name: 'image-zoom', fileName: file?.name })}
          aria-label='Zoom image'
        >
          <Icon name='otherZoom' aria-hidden />
        </ZoomButtonStyled>
      )}
    </>
  )
}

export default ImageElView
